// =============================================================================
// io/sync/reconcileRead.ts — the book, reduced to what reconciliation needs.
// -----------------------------------------------------------------------------
// Feeds `domain/reconcile.ts` (D153). Deliberately its own read rather than a
// widening of the import-history one, and the difference is the whole point:
//
// 🚨 `loadImportBatches` FILTERS SERVER-SIDE to trades carrying an
// `importBatchId`. Reconciliation must see the OPPOSITE rows too — the ones
// with no batch at all, which are the realtime logger's and the hand-typed
// ones. On 2026-08-05 the logger's eight rows are the only source that holds
// the day's largest loss, so a read that skipped them would have concluded the
// ATAS file was unique and offered to keep it.
//
// ⚠ NARROW ON PURPOSE. Ten columns, no `extras` blob beyond two sub-paths. The
// whole book crosses the wire here, and reconciliation needs a position's
// identity and its money — not its notes, screenshots or excursion walk.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { LIVE_SOURCE, type ReconcileTrade } from '../../domain/reconcile';
import { PAGE_ROWS } from './load';

/**
 * ⚠ ALIASED, and `importBatches.ts` records what happens when they are not:
 * PostgREST returns a column under its own name, so an unaliased `account_id`
 * arrives as `account_id` while the caller reads `accountId` — and every row
 * silently groups under `undefined`.
 */
const SELECT = [
  'id',
  'accountId:account_id',
  'date',
  'contract',
  'side',
  'qty',
  'timeIn:time_in',
  'netPnl:net_pnl',
  'batchId:extras->>importBatchId',
  'filename:extras->>importFilename',
].join(',');

interface Row {
  id: string | number;
  accountId: string | null;
  date: string | null;
  contract: string | null;
  side: string | null;
  qty: number | string | null;
  timeIn: string | null;
  netPnl: number | string | null;
  batchId: string | null;
  filename: string | null;
}

/**
 * Every trade in the book, shaped for reconciliation.
 *
 * ⚠ WHOLE BOOK, and unavoidably so: an import from March can still be fully
 * duplicated by one from July, and scoping to the active date range would make
 * the verdict on a batch change when the user moved a filter that has nothing
 * to do with it.
 *
 * ⚠ Rows with no side or no quantity are dropped here rather than downstream.
 * A position with no direction cannot be matched against anything, and letting
 * one through would make a source look unique on the strength of a row that
 * says nothing.
 */
export async function loadReconcileTrades(sb: SupabaseClient): Promise<ReconcileTrade[]> {
  const out: ReconcileTrade[] = [];

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('trades')
      .select(SELECT)
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    // ⚠ Throws rather than returning a short book. A partial read makes a source
    // look unique — the counterpart is simply on a page that never arrived — and
    // "safe to remove" computed from that is the one wrong answer this feature
    // must never give.
    if (error) {
      throw new Error(`reconciliation read failed on page ${(from / PAGE_ROWS) + 1}: ${error.message}`);
    }
    if (!data || !data.length) break;

    for (const r of data as unknown as Row[]) {
      const side = String(r.side ?? '').toLowerCase() === 'short' ? 'short' : 'long';
      const qty = Number(r.qty) || 0;
      if (!r.side || !qty || !r.date) continue;
      out.push({
        id: String(r.id),
        // ⚠ No batch id means the realtime logger or a hand-typed trade. That
        // is a SOURCE like any other, and the one the rules protect.
        sourceKey: r.batchId || LIVE_SOURCE,
        sourceLabel: r.filename || 'Realtime logger',
        account: String(r.accountId ?? 'unassigned'),
        date: String(r.date),
        contract: String(r.contract ?? ''),
        side,
        qty,
        timeIn: String(r.timeIn ?? ''),
        netPnl: Number(r.netPnl) || 0,
      });
    }

    if (data.length < PAGE_ROWS) break;
  }

  return out;
}
